import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import {
  formatQueryParams,
  JsonApiResponseDto,
  parseJsonApiResponse,
  prepareHeaders,
  Response,
  TQueryParams,
} from 'evolve-ui';

export enum TAdmissionAction {
  ADMITIR = 1,
  ANULAR = 2,
  ALTERAR = 3,
  CORRIGIR = 4,
}

export enum TAdmissionStatus {
  PENDING = 1, // Pendente
  DONE = 2, // Realizado
}

export enum TAdmissionAutomaticStatus {
  PENDING = 0, // Pendente
  SUCCESS = 1, // Sucesso
  TECHNICAL_ERROR = 2, // Erro técnico
  DATA_ERROR = 3, // Erro de dados
  INACTIVE = 4, // Envio Inativo
  MANUAL = 5, // Manual
  NON_ACCEPTED = 6, // Não aceite
}

export type TAdmission = {
  id: string;
  companyId: number;
  companyNiss: number;
  companyName: string;
  companyNif: string;
  companyAcronym: string;
  //agencyId: number;
  //employeeId?: number;
  //applicantId: number;
  //selectedEmployeeId: number;
  birthDate: Date;
  startDate: Date;
  endDate: Date;
  //customerId: number;
  //costCenterId: number;
  customerContractNumber?: string;
  employeeContractNumber?: string;
  //admissionStatusId: TAdmissionStatus;
  //admissionActionId: TAdmissionAction;
  errorDescription?: string;
  automaticStatus?: TAdmissionAutomaticStatus;
  socialSecurityNumber?: number | string;
  contractTypeId: number;
  workTypeOption: string;
  workTypeSS: string;
  contractTypeSSCode: string;
  professionCPPCode?: number;
  baseSalary: number;
  workPercentageSS: number;
  workHoursSS: number;
  workDaysSS: number;
  contractMotiveSSCode: string;
  nissToSubstitute?: number;
  regimeSSCode: string;
  jobLocationSSCode: number;
  admissionAction: TAdmissionAction;
  admissionStatus: TAdmissionStatus;
  fields: string;
  contractRegime: string;
  costCenter?: {
    id?: number;
    reference?: string;
    description?: string;
  };
  customer: {
    id?: number;
    number?: number;
    name?: string;
  };
  employee: {
    id?: number;
    code?: string;
    name?: string;
  };
  applicant: {
    id?: number;
    code?: string;
    name?: string;
  };
  selectedEmployee?: {
    id?: number;
    code?: string;
    name?: string;
  };
  professionCPP?: {
    code?: string;
    description?: string;
  };
  agency?: {
    id?: number;
    name?: string;
    acronym?: string;
  };
  created: Date;
  lastCommunication?: Date;
  hasPendingCessation?: boolean;
};

export const socialSecurityApi = createApi({
  reducerPath: 'socialSecurity',
  baseQuery: fetchBaseQuery({
    baseUrl: `${process.env.GATSBY_SOCIAL_SECURITY_API_URL}`,
    mode: 'cors',
    prepareHeaders,
  }),
  tagTypes: ['Admissions'],
  endpoints: (builder) => ({
    getAdmissions: builder.query<Response<TAdmission>, TQueryParams>({
      query: (params) => formatQueryParams(params, '/admissions'),
      providesTags: ['Admissions'],
      transformResponse: (res, meta, args) => {
        return parseJsonApiResponse<TAdmission>(
          res as JsonApiResponseDto<TAdmission>,
          meta,
          args,
          true,
        );
      },
    }),
    getSingleAdmission: builder.query<
      Response<TAdmission>,
      { admissionId: string }
    >({
      providesTags: ['Admissions'],
      query: ({ admissionId }) => `/admissions/${admissionId}`,
      transformResponse: (res, meta, args) => {
        return parseJsonApiResponse<TAdmission>(
          res as JsonApiResponseDto<TAdmission>,
          meta,
          args,
          true,
        );
      },
    }),
    markAdmissionAsDone: builder.mutation({
      invalidatesTags: ['Admissions'],
      query: (body) => ({
        url: `/admissions/manual`,
        method: 'POST',
        body,
      }),
    }),
    sendAdmissionsToSocialSecurity: builder.mutation({
      invalidatesTags: ['Admissions'],
      query: (body) => ({
        url: `/admissions`,
        method: 'POST',
        body,
      }),
    }),
    deleteAdmissions: builder.mutation({
      invalidatesTags: ['Admissions'],
      query: (admissionId) => ({
        url: `/admissions/${admissionId}`,
        method: 'DELETE',
      }),
    }),
    syncAdmissionEmployee: builder.mutation({
      invalidatesTags: ['Admissions'],
      query: (admissionId) => ({
        url: `/admissions/${admissionId}/sync`,
        method: 'POST',
      }),
    }),
  }),
});

export const {
  useGetAdmissionsQuery,
  useGetSingleAdmissionQuery,
  useMarkAdmissionAsDoneMutation,
  useSendAdmissionsToSocialSecurityMutation,
  useDeleteAdmissionsMutation,
  useSyncAdmissionEmployeeMutation,
} = socialSecurityApi;
